import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Tooltip } from "@sylviq/ui-react-web/components/ui/tooltip";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { CircleHelp } from "lucide-react";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ExtensionV2CostingReview } from "@/features/close/ExtensionV2CostingReview";
import { useSession } from "@/lib/session";
import { CultivationCapabilityNotice } from "./CultivationCapabilityNotice";
import { capabilityAllowed } from "./cultivation-capabilities";
import { dateLabel } from "./cultivation-dates";
import { diffReviewValues, reviewIsReadOnly } from "./cultivation-review";
import {
  closeStatus,
  evidenceCoverage,
  profileLabel,
  statusLabel,
  statusTone,
} from "./cultivation-readiness";
import type { CultivationWorkspace } from "./cultivation-types";

const SETTINGS_LOCK_SEARCH = { tab: "books", section: "period-locks" } as const;

function workpaperViewId(workspace: CultivationWorkspace): string | null {
  for (const value of workspace.installation?.v2?.ui ?? []) {
    if (!value || typeof value !== "object") continue;
    const contribution = value as {
      app?: unknown;
      slot?: unknown;
      viewId?: unknown;
    };
    if (
      contribution.app === "niche-accounting" &&
      contribution.slot === "accounting.workpaper-panel.v1" &&
      typeof contribution.viewId === "string" &&
      contribution.viewId
    ) {
      return contribution.viewId;
    }
  }
  return null;
}

export function evidenceKey(workspace: CultivationWorkspace): string {
  const imports = workspace.imports.imports
    .filter((item) => item.license_id === workspace.licenseId)
    .map(
      (item) =>
        `${item.source_document_id}:${item.checksum_sha256}:${item.revision}:${item.period_start}:${item.period_end}`,
    )
    .sort();
  const attestations = workspace.imports.attestations
    .filter((item) => item.license_id === workspace.licenseId)
    .map((item) => `${item.profile}:${item.month}:${item.reason}`)
    .sort();
  return [...imports, ...attestations].join("|");
}

export function CultivationClose({
  workspace,
  onOpenImports,
  initialValues,
  draftOverrides,
  onDraftChange,
  draft,
}: {
  workspace: CultivationWorkspace;
  onOpenImports: () => void;
  initialValues?: Record<string, string> | null;
  draftOverrides?: Record<string, string> | null;
  onDraftChange?: (values: Record<string, string>) => void;
  draft?: Record<string, string>;
}) {
  const { state: session } = useSession();
  const [notice, setNotice] = useState<string | null>(null);
  const [approvalNote, setApprovalNote] = useState("");
  const [approvalBusy, setApprovalBusy] = useState<"approve" | "return" | null>(
    null,
  );
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const review = workspace.installation
    ? (workspace.reviews.find(
        (item) => item.installation_id === workspace.installation!.id,
      ) ?? null)
    : null;
  const savedValues =
    workspace.reviewDetail?.review.review_id === review?.review_id
      ? (workspace.reviewDetail?.form_values ?? null)
      : null;
  const coverage = evidenceCoverage(
    workspace.imports.imports,
    workspace.licenseId,
    workspace.periodStart,
    workspace.periodEnd,
    workspace.imports.attestations,
  );
  const status = closeStatus(review, coverage);
  const viewId = workpaperViewId(workspace);
  const reviewBlocked =
    review?.state === "failed" || review?.state === "correction_required";
  const readOnly = reviewIsReadOnly(review);
  const canView = capabilityAllowed(workspace.capabilities, "view");
  const canReview = capabilityAllowed(workspace.capabilities, "review");
  const canApprove = capabilityAllowed(workspace.capabilities, "approve");
  const formDisabled = readOnly || !canReview;
  const changed = useMemo(
    () => diffReviewValues(savedValues, draft ?? {}),
    [draft, savedValues],
  );
  const approvalState = review?.approval_state ?? "not_required";
  const approvalRequired = review?.approval_required === true;
  const approvalStale = approvalState === "approved" && changed.length > 0;
  const actorId = session.status === "authenticated" ? session.user.id : null;
  const isMaker =
    review?.reviewed_by_user_id !== undefined
      ? String(review.reviewed_by_user_id) === actorId
      : false;
  const submitLabel = review?.approval_required
    ? "Save and send for approval"
    : "Submit reviewed inputs";

  async function decideApproval(kind: "approve" | "return") {
    if (!review) return;
    if (kind === "return" && !approvalNote.trim()) {
      setApprovalError("A return note is required.");
      return;
    }
    setApprovalBusy(kind);
    setApprovalError(null);
    setNotice(null);
    try {
      if (kind === "approve") {
        await workspace.approveReview(review.review_id, approvalNote);
        setNotice("The review revision is approved. Posting is now available.");
      } else {
        await workspace.returnReview(review.review_id, approvalNote.trim());
        setNotice("The review was returned to the maker with your note.");
      }
      setApprovalNote("");
    } catch (cause) {
      setApprovalError(
        cause instanceof Error
          ? cause.message
          : "The approval decision could not be recorded.",
      );
    } finally {
      setApprovalBusy(null);
    }
  }

  if (!canView) {
    return (
      <CultivationCapabilityNotice
        capability="view"
        action="open the cultivation close review"
      />
    );
  }

  if (!workspace.installation) {
    return (
      <EmptyState
        title="Cultivation costing is not installed"
        reason="Install and enable the Niche cultivation costing extension before opening the close review."
      />
    );
  }

  if (!workspace.licenseId) {
    return (
      <EmptyState
        title="Choose a location"
        reason="The close review needs one active report license. Choose a location above; if none is listed, retrieve facilities in the extension's METRC settings."
        action={
          <Button asChild variant="secondary">
            <Link to="/settings">Open Accounting settings</Link>
          </Button>
        }
      />
    );
  }

  if (!viewId) {
    return (
      <EmptyState
        title="Close review is unavailable"
        reason="The installed extension does not declare a workpaper close view. Update the extension before reviewing this close."
      />
    );
  }

  return (
    <div className="grid gap-4">
      <section className="surface flex flex-wrap items-start gap-3 rounded-2xl border border-[var(--border)] p-4">
        <div className="grid gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight">
              Close review
            </h2>
            <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>
            <TooltipPrimitive.Provider>
              <Tooltip label="Inputs are prefilled from imported METRC evidence and the prior close where available. Review the values, fix exceptions, then submit. Saving does not post or lock.">
                <button
                  type="button"
                  aria-label="About close review inputs"
                  className="inline-flex size-5 items-center justify-center rounded-full text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                >
                  <CircleHelp className="size-4" aria-hidden="true" />
                </button>
              </Tooltip>
            </TooltipPrimitive.Provider>
          </div>
          <p className="text-sm text-[var(--muted-foreground)]">
            {workspace.periodStart} to {workspace.periodEnd}
            {review
              ? ` · Saved ${dateLabel(review.reviewed_at.slice(0, 10))} by ${
                  review.reviewed_by_user_id
                    ? review.reviewed_by_user_id.slice(0, 8)
                    : "unknown"
                }${
                  review.review_revision
                    ? ` · revision ${review.review_revision}`
                    : ""
                }`
              : " · Nothing saved yet"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 ms-auto">
          <Button type="button" variant="secondary" onClick={onOpenImports}>
            {coverage.ready ? "View evidence" : "Import missing reports"}
          </Button>
          <Button asChild variant="outline">
            <Link to="/settings" search={SETTINGS_LOCK_SEARCH}>
              Post and lock
            </Link>
          </Button>
        </div>
        {!coverage.ready ? (
          <p role="alert" className="w-full text-sm text-[var(--warning)]">
            Missing{" "}
            {coverage.missing
              .slice(0, 4)
              .map((cell) => `${profileLabel(cell.profile)} for ${cell.month}`)
              .join(", ")}
            {coverage.missing.length > 4
              ? `, and ${coverage.missing.length - 4} more`
              : ""}
            .
          </p>
        ) : null}
        {!canReview ? (
          <CultivationCapabilityNotice
            capability="review"
            action="save or change this close review"
          />
        ) : null}
      </section>

      {review?.approval_required ? (
        <section
          className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5"
          aria-label="Maker-checker approval"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">Maker-checker approval</h3>
            <Badge
              tone={
                approvalState === "approved"
                  ? "success"
                  : approvalState === "rejected"
                    ? "danger"
                    : "warning"
              }
            >
              {approvalState.replaceAll("_", " ")}
            </Badge>
            {approvalStale ? (
              <Badge tone="danger">Approval stale after edits</Badge>
            ) : null}
          </div>
          {review.approval_reasons?.length ? (
            <ul className="grid gap-1 text-sm text-[var(--muted-foreground)]">
              {review.approval_reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
          <p className="text-sm text-[var(--muted-foreground)]">
            {approvalState === "approved"
              ? `Approved${review.approved_by_user_id ? ` by user ${review.approved_by_user_id.slice(0, 8)}` : ""}${review.approved_at ? ` at ${review.approved_at}` : ""}${review.approval_note ? ` · ${review.approval_note}` : ""}.`
              : approvalState === "rejected"
                ? `Returned${review.approval_note ? `: ${review.approval_note}` : "."} The maker can correct the inputs and send a new revision.`
                : "Saving this revision sends it to a checker. Posting stays blocked until the same revision is approved."}
          </p>
          {approvalStale ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              The saved approval no longer matches this draft. Save and request
              a new approval before posting.
            </p>
          ) : null}
          {review.approval_state === "pending" ||
          review.approval_state === "rejected" ? (
            isMaker ? (
              <p className="text-sm text-[var(--warning)]">
                A maker cannot approve their own review. Ask another checker in
                Console roles.
              </p>
            ) : canApprove ? (
              <div className="grid gap-2">
                <label className="grid gap-1 text-sm">
                  Decision note{" "}
                  {review.approval_state === "rejected"
                    ? "(required to return)"
                    : "(optional to approve)"}
                  <textarea
                    className="min-h-20 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-3 text-sm"
                    value={approvalNote}
                    onChange={(event) => setApprovalNote(event.target.value)}
                    placeholder="Record the approval basis or the reason the review is returned"
                    disabled={approvalBusy !== null}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={() => void decideApproval("approve")}
                    disabled={approvalBusy !== null}
                  >
                    {approvalBusy === "approve"
                      ? "Approving…"
                      : "Approve revision"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void decideApproval("return")}
                    disabled={approvalBusy !== null}
                  >
                    {approvalBusy === "return"
                      ? "Returning…"
                      : "Return to maker"}
                  </Button>
                </div>
              </div>
            ) : (
              <CultivationCapabilityNotice
                capability="approve"
                action="approve or return this review"
              />
            )
          ) : null}
          {approvalError ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {approvalError}
            </p>
          ) : null}
          {approvalRequired && approvalState !== "approved" ? (
            <p className="text-xs text-[var(--warning)]">
              Posting is blocked until this revision is approved.
            </p>
          ) : null}
        </section>
      ) : null}

      <ExtensionV2CostingReview
        key={`${workspace.licenseId}|${workspace.periodStart}|${workspace.periodEnd}|${review?.review_id ?? "none"}|${review?.review_revision ?? 0}`}
        installationId={workspace.installation.id}
        viewId={viewId}
        context={{
          license_id: workspace.licenseId,
          period: { start: workspace.periodStart, end: workspace.periodEnd },
        }}
        dependencyInstallationId={null}
        disabled={formDisabled}
        initialValues={initialValues}
        draftOverrides={draftOverrides}
        savedValues={savedValues}
        evidenceKey={evidenceKey(workspace)}
        review={review}
        submitLabel={submitLabel}
        onDraftChange={onDraftChange}
        onOpenImports={onOpenImports}
        hostBlockers={[
          ...(coverage.missing.length > 0
            ? [
                `Missing required evidence for ${coverage.missing.length} report-month${coverage.missing.length === 1 ? "" : "s"}.`,
              ]
            : []),
          ...(reviewBlocked
            ? ["The last posting failed or needs a correction."]
            : []),
          ...(review?.approval_required && review.approval_state !== "approved"
            ? ["Posting is blocked until this review revision is approved."]
            : []),
        ]}
        onReviewed={async (inputs, formValues, approval) => {
          if (!inputs)
            throw new Error("The extension did not return reviewed inputs.");
          await workspace.saveReviewedInputs(
            inputs,
            formValues,
            approval,
          );
          setNotice(
            approval?.approval_required
              ? "Reviewed inputs saved and sent for approval. Posting is blocked until a checker approves this revision."
              : "Reviewed inputs saved. Post them from Settings, then lock the period.",
          );
        }}
      />

      {notice ? (
        <p role="status" className="text-sm text-[var(--muted-foreground)]">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
